/**
 * Login Page
 * Phone number entry and OTP request for candidates
 * Employee ID and password for HR/HRO users
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '160249576799-6s2h87rcirpkr3s7o18k6qabv5a4pmue.apps.googleusercontent.com';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const LoginPage = () => {
  const location = useLocation();
  const [loginType, setLoginType] = useState(location.state?.loginType || 'candidate'); // 'candidate' or 'hr'
  const [email, setEmail] = useState('');
  const [candidatePassword, setCandidatePassword] = useState('');
  const [hrPhoneNumber, setHrPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Set-password popup state (for old candidates with no password)
  const [showSetPasswordPopup, setShowSetPasswordPopup] = useState(false);
  const [setPasswordEmail, setSetPasswordEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [setPasswordError, setSetPasswordError] = useState('');
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  // Forgot-password popup state (candidate)
  const [showForgotPopup, setShowForgotPopup] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = enter email, 2 = enter OTP + new password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const googleButtonRef = useRef(null);
  const googleRenderedRef = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (location.state?.loginType) {
      setLoginType(location.state.loginType);
    }
  }, [location.state]);

  const registrationSuccess = location.state?.registrationSuccess;

  const handleCandidateLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email address is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!candidatePassword) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.loginCandidateWithPassword(email, candidatePassword);
      if (response.success) {
        login(response.user, response.token, response.refreshToken);
        navigate('/candidate/dashboard');
      } else if (response.requiresPasswordSetup) {
        setSetPasswordEmail(email);
        setShowSetPasswordPopup(true);
      } else {
        setError(response.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresPasswordSetup) {
        setSetPasswordEmail(email);
        setShowSetPasswordPopup(true);
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setSetPasswordError('');

    if (!newPassword || newPassword.length < 6) {
      setSetPasswordError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setSetPasswordError('Passwords do not match');
      return;
    }

    setSetPasswordLoading(true);
    try {
      const response = await authService.setCandidatePassword(setPasswordEmail, newPassword);
      if (response.success) {
        login(response.user, response.token, response.refreshToken);
        navigate('/candidate/dashboard');
      } else {
        setSetPasswordError(response.error || 'Failed to set password. Please try again.');
      }
    } catch (err) {
      setSetPasswordError(err.response?.data?.error || 'Failed to set password. Please try again.');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const openForgotPopup = () => {
    setForgotStep(1);
    setForgotEmail('');
    setForgotOtp('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotError('');
    setShowForgotPopup(true);
  };

  const closeForgotPopup = () => {
    setShowForgotPopup(false);
    setForgotError('');
  };

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    setForgotError('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!forgotEmail || !emailRegex.test(forgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await authService.sendEmailOTP(forgotEmail);
      if (response.success) {
        setForgotStep(2);
      } else {
        setForgotError(response.error || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setForgotError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotResetPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotOtp || forgotOtp.length !== 6) {
      setForgotError('Please enter the 6-digit OTP');
      return;
    }
    if (!forgotNewPassword || forgotNewPassword.length < 6) {
      setForgotError('Password must be at least 6 characters');
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('Passwords do not match');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await authService.resetCandidatePassword(forgotEmail, forgotOtp, forgotNewPassword);
      if (response.success) {
        closeForgotPopup();
        login(response.user, response.token, response.refreshToken);
        navigate('/candidate/dashboard');
      } else {
        setForgotError(response.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setForgotError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleHRLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!hrPhoneNumber || !password) {
      setError('Phone number and password are required');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.loginHREmployeeByPhone(hrPhoneNumber, password);

      if (response.success) {
        // Login successful, store tokens and redirect
        login(response.user, response.token, response.refreshToken);

        // Redirect based on role
        if (response.user.role === 'HR') {
          navigate('/hr/dashboard');
        } else if (response.user.role === 'HRO') {
          navigate('/hro/dashboard');
        } else if (response.user.role === 'ADMIN') {
          navigate('/admin/dashboard');
        } else if (response.user.role === 'CHAIRMAN') {
          navigate('/chairman/dashboard');
        }
      } else {
        setError(response.error || 'Invalid credentials');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (response) => {
    if (!response?.credential) {
      setError('Google login was cancelled or failed');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await authService.loginWithGoogle(response.credential);

      if (result.success) {
        login(result.user, result.token, result.refreshToken);
        navigate('/candidate/dashboard');
      } else {
        setError(result.error || 'Google login failed');
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError(err.response?.data?.error || 'Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate]);

  useEffect(() => {
    if (loginType !== 'candidate' || !googleButtonRef.current) {
      googleRenderedRef.current = false;
      return;
    }

    const renderGoogleButton = () => {
      if (!window.google?.accounts?.id || !googleButtonRef.current || googleRenderedRef.current) {
        return;
      }
      googleRenderedRef.current = true;
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        shape: 'rectangular',
        text: 'signin_with',
        width: googleButtonRef.current.offsetWidth || 360
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return;
    }

    let script = document.querySelector(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = GOOGLE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    script.addEventListener('load', renderGoogleButton);

    return () => {
      script.removeEventListener('load', renderGoogleButton);
      googleRenderedRef.current = false;
    };
  }, [handleGoogleCredential, loginType]);

  return (
    <div className={`flex min-h-screen bg-background relative overflow-hidden
      ${loginType === 'hr' ? 'h-screen justify-stretch items-stretch p-0 overflow-hidden' : 'justify-center items-center p-lg'}
      before:content-[''] before:absolute before:top-[-10%] before:left-[-5%] before:w-[300px] before:h-[300px] 
      before:bg-accent-green before:rounded-full before:opacity-[0.06] before:animate-floatSlow before:z-0
      after:content-[''] after:absolute after:bottom-[-10%] after:right-[-5%] after:w-[400px] after:h-[400px] 
      after:bg-accent-terracotta after:rounded-full after:opacity-[0.06] after:animate-floatSlowReverse after:z-0`}>
      <div className={`bg-white w-full relative z-[1] border border-[rgba(46,46,46,0.08)] animate-slideUp overflow-hidden ${loginType === 'hr' ? 'max-w-none h-screen rounded-none border-x-0 border-y-0 shadow-none overflow-hidden' : 'max-w-[420px] rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.08)]'
        }`}>
        <div className={loginType === 'hr' ? 'grid h-full grid-cols-1 lg:grid-cols-[1.12fr_0.88fr]' : ''}>
          {loginType === 'hr' && (
            <div className="hidden lg:flex h-full items-center justify-center bg-[#FAFAFA] border-r border-border-light p-8 xl:p-10">
              <img
                src="/hrpolicy.jpg"
                alt="HR Policy"
                className="w-full max-w-[620px] h-auto max-h-[82vh] rounded-xl object-contain"
              />
            </div>
          )}

          <div className={loginType === 'hr' ? 'flex h-full items-center justify-center px-6 py-6 sm:px-10 lg:px-12 xl:px-16' : 'p-2xl'}>
            <div className={loginType === 'hr' ? 'w-full max-w-[460px]' : ''}>
              {loginType === 'hr' && (
                <div className="mb-lg lg:hidden bg-[#FAFAFA] border border-border-light rounded-xl p-3">
                  <img
                    src="/hrpolicy.jpg"
                    alt="HR Policy"
                    className="w-full max-h-[300px] object-contain rounded-lg"
                  />
                </div>
              )}

              <div className="flex flex-col items-center mb-md">
                <img
                  src="/logo_final.png"
                  alt="Jupiter International logo"
                  className="w-16 h-16 object-contain mb-3"
                />
                <p className="text-[13px] font-medium text-text-secondary tracking-[0.4px] uppercase">
                  Jupiter International
                </p>
              </div>

              <h2 className="text-[15px] text-text-secondary mb-xl text-center font-normal leading-[1.5]">
                Login
              </h2>

              {/* Login Type Tabs */}
              <div className="flex mb-lg border-b border-border-light">
                <button
                  type="button"
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${loginType === 'candidate'
                    ? 'border-accent-green text-accent-green'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  onClick={() => {
                    setLoginType('candidate');
                    setError('');
                  }}
                >
                  Candidate Login
                </button>
                <button
                  type="button"
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${loginType === 'hr'
                    ? 'border-accent-green text-accent-green'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  onClick={() => {
                    setLoginType('hr');
                    setError('');
                  }}
                >
                  HR/Client Login
                </button>
              </div>

              {/* Candidate Login Form */}
              {loginType === 'candidate' && (
                <form onSubmit={handleCandidateLogin}>
                  {registrationSuccess && (
                    <div className="bg-[rgba(107,127,92,0.1)] text-accent-green py-[14px] px-4 rounded-md mb-lg
                    text-[14px] border-l-[3px] border-accent-green leading-[1.5]">
                      Registration successful! Please log in with your email and password.
                    </div>
                  )}
                  <div className="mb-lg">
                    <label htmlFor="email" className="block mb-sm text-text-primary font-medium text-[14px]">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      disabled={isLoading}
                      autoFocus
                      className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                    transition-all duration-200 box-border bg-surface text-text-primary
                    focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                    disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </div>

                  <div className="mb-lg">
                    <label htmlFor="candidatePassword" className="block mb-sm text-text-primary font-medium text-[14px]">
                      Password
                    </label>
                    <input
                      type="password"
                      id="candidatePassword"
                      value={candidatePassword}
                      onChange={(e) => setCandidatePassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={isLoading}
                      className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                    transition-all duration-200 box-border bg-surface text-text-primary
                    focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                    disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </div>

                  {error && (
                    <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg
                text-[14px] border-l-[3px] border-status-error leading-[1.5]">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md
                text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
                active:translate-y-0 active:enabled:translate-y-0
                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Login'}
                  </button>

                  <div className="flex items-center gap-3 my-lg">
                    <div className="h-px flex-1 bg-border-light" />
                    <span className="text-[12px] uppercase text-text-tertiary tracking-[0.4px]">or</span>
                    <div className="h-px flex-1 bg-border-light" />
                  </div>

                  <div
                    ref={googleButtonRef}
                    className={`min-h-[44px] flex justify-center ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
                  />

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={openForgotPopup}
                      className="text-sm text-accent-green hover:text-accent-green-dark transition-colors cursor-pointer bg-transparent border-none underline"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              )}

              {/* Set Password Popup for old candidates */}
              {showSetPasswordPopup && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-[400px] p-8 animate-slideUp">
                    <div className="flex flex-col items-center mb-lg">
                      <img src="/logo_final.png" alt="Jupiter International logo" className="w-12 h-12 object-contain mb-3" />
                      <h3 className="text-[18px] font-semibold text-text-primary">Set Your Password</h3>
                      <p className="text-[13px] text-text-secondary mt-1 text-center">
                        Your account doesn't have a password yet. Please set one to continue.
                      </p>
                    </div>

                    <form onSubmit={handleSetPassword}>
                      <div className="mb-lg">
                        <label className="block mb-sm text-text-primary font-medium text-[14px]">New Password</label>
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="At least 6 characters"
                          disabled={setPasswordLoading}
                          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                          transition-all duration-200 bg-surface text-text-primary
                          focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                          disabled:opacity-60"
                        />
                      </div>

                      <div className="mb-lg">
                        <label className="block mb-sm text-text-primary font-medium text-[14px]">Confirm Password</label>
                        <input
                          type="password"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          placeholder="Repeat your password"
                          disabled={setPasswordLoading}
                          className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                          transition-all duration-200 bg-surface text-text-primary
                          focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                          disabled:opacity-60"
                        />
                      </div>

                      {setPasswordError && (
                        <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg
                        text-[14px] border-l-[3px] border-status-error leading-[1.5]">
                          {setPasswordError}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={setPasswordLoading}
                        className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md
                        text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                        hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {setPasswordLoading ? 'Setting Password...' : 'Set Password & Login'}
                      </button>

                      <button
                        type="button"
                        onClick={() => { setShowSetPasswordPopup(false); setSetPasswordError(''); setNewPassword(''); setConfirmNewPassword(''); }}
                        className="w-full mt-3 py-3 text-sm text-text-secondary hover:text-text-primary transition-colors bg-transparent border-none cursor-pointer"
                      >
                        Cancel
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* Forgot Password Popup for candidates */}
              {showForgotPopup && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl shadow-xl w-full max-w-[400px] p-8 animate-slideUp">
                    <div className="flex flex-col items-center mb-lg">
                      <img src="/logo_final.png" alt="Jupiter International logo" className="w-12 h-12 object-contain mb-3" />
                      <h3 className="text-[18px] font-semibold text-text-primary">Reset Password</h3>
                      <p className="text-[13px] text-text-secondary mt-1 text-center">
                        {forgotStep === 1
                          ? 'Enter your registered email address to receive an OTP.'
                          : `OTP sent to ${forgotEmail}. Enter it below along with your new password.`}
                      </p>
                    </div>

                    {forgotStep === 1 ? (
                      <form onSubmit={handleForgotSendOtp}>
                        <div className="mb-lg">
                          <label className="block mb-sm text-text-primary font-medium text-[14px]">Email Address</label>
                          <input
                            type="email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            placeholder="your.email@example.com"
                            disabled={forgotLoading}
                            autoFocus
                            className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                            transition-all duration-200 bg-surface text-text-primary
                            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                            disabled:opacity-60"
                          />
                        </div>

                        {forgotError && (
                          <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg
                          text-[14px] border-l-[3px] border-status-error leading-[1.5]">
                            {forgotError}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={forgotLoading}
                          className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md
                          text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                          hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {forgotLoading ? 'Sending OTP...' : 'Send OTP'}
                        </button>

                        <button
                          type="button"
                          onClick={closeForgotPopup}
                          className="w-full mt-3 py-3 text-sm text-text-secondary hover:text-text-primary transition-colors bg-transparent border-none cursor-pointer"
                        >
                          Cancel
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleForgotResetPassword}>
                        <div className="mb-lg">
                          <label className="block mb-sm text-text-primary font-medium text-[14px]">OTP Code</label>
                          <input
                            type="text"
                            value={forgotOtp}
                            onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="6-digit OTP"
                            disabled={forgotLoading}
                            autoFocus
                            className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                            transition-all duration-200 bg-surface text-text-primary text-center tracking-[6px]
                            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                            disabled:opacity-60"
                          />
                        </div>

                        <div className="mb-lg">
                          <label className="block mb-sm text-text-primary font-medium text-[14px]">New Password</label>
                          <input
                            type="password"
                            value={forgotNewPassword}
                            onChange={(e) => setForgotNewPassword(e.target.value)}
                            placeholder="At least 6 characters"
                            disabled={forgotLoading}
                            className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                            transition-all duration-200 bg-surface text-text-primary
                            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                            disabled:opacity-60"
                          />
                        </div>

                        <div className="mb-lg">
                          <label className="block mb-sm text-text-primary font-medium text-[14px]">Confirm Password</label>
                          <input
                            type="password"
                            value={forgotConfirmPassword}
                            onChange={(e) => setForgotConfirmPassword(e.target.value)}
                            placeholder="Repeat your password"
                            disabled={forgotLoading}
                            className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px]
                            transition-all duration-200 bg-surface text-text-primary
                            focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                            disabled:opacity-60"
                          />
                        </div>

                        {forgotError && (
                          <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg
                          text-[14px] border-l-[3px] border-status-error leading-[1.5]">
                            {forgotError}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={forgotLoading}
                          className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md
                          text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                          hover:bg-accent-green-dark disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {forgotLoading ? 'Resetting...' : 'Reset Password & Login'}
                        </button>

                        <button
                          type="button"
                          onClick={() => setForgotStep(1)}
                          className="w-full mt-3 py-3 text-sm text-text-secondary hover:text-text-primary transition-colors bg-transparent border-none cursor-pointer"
                        >
                          Back
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              )}

              {/* HR Login Form */}
              {loginType === 'hr' && (
                <form onSubmit={handleHRLogin}>
                  <div className="mb-lg">
                    <label htmlFor="hrPhoneNumber" className="block mb-sm text-text-primary font-medium text-[14px]">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      id="hrPhoneNumber"
                      value={hrPhoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                        if (value.length <= 15) {
                          setHrPhoneNumber(value);
                        }
                      }}
                      placeholder="Enter phone number"
                      maxLength="15"
                      disabled={isLoading}
                      autoFocus
                      className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                  transition-all duration-200 box-border bg-surface text-text-primary
                  focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                  disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
                      Enter your mobile number (4-15 digits)
                    </small>
                  </div>

                  <div className="mb-lg">
                    <label htmlFor="password" className="block mb-sm text-text-primary font-medium text-[14px]">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={isLoading}
                      className="w-full py-[14px] px-4 border-[1.5px] border-border rounded-md text-[15px] 
                  transition-all duration-200 box-border bg-surface text-text-primary
                  focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]
                  disabled:bg-background-light disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
                      Use the password provided by your administrator
                    </small>
                  </div>

                  {error && (
                    <div className="bg-status-error-bg text-status-error py-[14px] px-4 rounded-md mb-lg 
                text-[14px] border-l-[3px] border-status-error leading-[1.5]">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-[14px] px-6 bg-accent-green text-surface border-none rounded-md 
                text-[15px] font-semibold cursor-pointer transition-all duration-200 shadow-sm
                hover:bg-accent-green-dark hover:-translate-y-[1px] hover:shadow-md hover:enabled:bg-accent-green-dark
                active:translate-y-0 active:enabled:translate-y-0
                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Login'}
                  </button>

                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="text-sm text-accent-green hover:text-accent-green-dark transition-colors cursor-pointer bg-transparent border-none underline"
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              )}

              {/* Registration Link - Only show for candidate login */}
              {loginType === 'candidate' && (
                <div className="mt-xl pt-lg border-t border-border-light text-center">
                  <p className="text-text-secondary text-[14px] mb-md">
                    Don't have an account?
                  </p>
                  <button
                    className="w-full py-[14px] px-6 bg-surface text-accent-green border-[1.5px] border-accent-green 
                rounded-md text-[15px] font-semibold cursor-pointer transition-all duration-200
                hover:bg-accent-green hover:text-surface hover:-translate-y-[1px] hover:shadow-md
                active:translate-y-0"
                    onClick={() => navigate('/register')}
                  >
                    Register as Candidate
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
